import { CancelToken, isCancel } from 'axios';
import PropTypes from 'prop-types';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Button, UncontrolledTooltip } from 'reactstrap';

import CompleteMaintenanceModal from './CompleteMaintenanceModal';
import { BrandPrimarySpinningCircle } from '../LoadingCircle';
import { DUE, UPCOMING } from '../../Constants/GatewayReminderInstances';
import {
  ALL_TYPE,
  CALENDAR_TYPE,
  SIGNAL_TYPE,
} from '../../Constants/GtemplateReminder';
import {
  generateCalendarTypeProps,
  generateSignalTypeProps,
} from '../../Lib/GatewayReminderInstance';
import GatewayReminderInstanceService from '../../Services/GatewayReminderInstanceService';
import { formatLocalMonthDayYear } from '../../Utils/dateFormatHelpers.util';

export default memo(MaintenanceWidget);

const initState = {
  error: '',
  instances: [],
  instancesLoading: true,
  showCompleteMaintenanceModal: '',
};

function MaintenanceWidget({ asset, shadow, signalUnitsMap, widget }) {
  const api = useMemo(() => new GatewayReminderInstanceService(), []),
    reminderIdSet = useMemo(
      () =>
        widget?.settings?.maintenance?.reduce?.(
          (set, { reminder_id }) => set.add(reminder_id),
          new Set()
        ),
      [widget?.settings?.maintenance]
    ),
    refreshSource = CancelToken.source();
  const [state, updateState] = useState(initState);

  const fetchReminderInstances = useCallback(
      async (source) => {
        try {
          updateState(initState);

          const params = { gateway_id: asset?._id, limit: 500, page: 1 };
          const { data: instances } =
            await api.getPaginatedGatewayReminderInstances(
              params,
              source.token
            );
          updateState((prevState) => ({
            ...prevState,
            error: '',
            instances,
            instancesLoading: false,
          }));
        } catch (err) {
          if (isCancel(err)) return;
          const error = err.response?.data?.message ?? err.message;
          updateState((prevState) => ({
            ...prevState,
            error,
            instancesLoading: false,
          }));
        }
      },
      [api, asset?._id]
    ),
    toggleCompleteMaintenanceModal = useCallback(
      (showCompleteMaintenanceModal = '') => {
        updateState((prevState) => ({
          ...prevState,
          showCompleteMaintenanceModal,
        }));
      },
      []
    );

  const shadowValueBySignalIdMap = useMemo(
      () =>
        shadow?.signalCollections?.reduce?.((map, col) => {
          if (!Array.isArray(col?.signals) || !col?.signals?.length) {
            return map;
          }

          return col.signals.reduce((acc, s) => acc.set(s._id, s.value), map);
        }, new Map()) ?? new Map(),
      [shadow]
    ),
    sortedInstances = useMemo(
      () =>
        state.instances
          ?.filter?.((instance) =>
            reminderIdSet.has(instance?.GtemplateReminder?._id)
          )
          ?.sort?.((a, b) => {
            const {
                MaintenanceNotifications: [
                  { maintenance_completed, status } = {},
                ] = [],
              } = a ?? {},
              { MaintenanceNotifications: [{ status: s2 } = {}] = [] } =
                b ?? {};
            switch (true) {
              case maintenance_completed:
              case status === UPCOMING && s2 === DUE:
                return 1;
              case status === DUE:
              case status === UPCOMING && s2 !== DUE:
                return -1;
              default:
                return 0;
            }
          })
          ?.map?.((instance) => (
            <div className="row" key={instance?._id}>
              <p
                className="mb-2 col-6 pl-4 nine-tenth-text text-muted text-truncate"
                id={`reminder-name-${instance?._id}`}
              >
                {instance?.GtemplateReminder?.name}
                <UncontrolledTooltip
                  innerClassName="three-quarter-text rounded-pill"
                  target={`reminder-name-${instance?._id}`}
                >
                  {instance?.GtemplateReminder?.name}
                </UncontrolledTooltip>
              </p>
              <div className="col-3 d-flex align-items-center justify-content-center mb-2 px-0">
                <TimeLengthButton
                  instance={instance}
                  toggleCompleteMaintenanceModal={
                    toggleCompleteMaintenanceModal
                  }
                />
              </div>
              <p className="mb-2 col-3 d-flex align-items-center justify-content-center nine-tenth-text text-muted pl-0">
                {formatLocalMonthDayYear(
                  instance?.MaintenanceHistoryLogs?.[0]?.service_date
                )}
              </p>
            </div>
          )) ?? null,
      [reminderIdSet, state.instances, toggleCompleteMaintenanceModal]
    );

  const checkedInstances = useMemo(
      () =>
        state.instances?.reduce?.(
          (map, instance) =>
            state.showCompleteMaintenanceModal === instance._id
              ? map.set(instance._id, instance)
              : map,
          new Map()
        ) ?? new Map(),
      [state.instances, state.showCompleteMaintenanceModal]
    ),
    instanceShadowValueMap = useMemo(() => {
      const now = Date.now();
      return (
        state.instances?.reduce?.((map, instance) => {
          switch (instance.GtemplateReminder.type) {
            case ALL_TYPE: {
              const calendarProps = generateCalendarTypeProps(instance, now);
              const signalProps = generateSignalTypeProps(
                instance,
                shadowValueBySignalIdMap
              );
              return map.set(instance._id, {
                ...calendarProps,
                ...signalProps,
              });
            }
            case CALENDAR_TYPE:
              return map.set(
                instance._id,
                generateCalendarTypeProps(instance, now)
              );
            case SIGNAL_TYPE:
              return map.set(
                instance._id,
                generateSignalTypeProps(instance, shadowValueBySignalIdMap)
              );
            default:
              break;
          }
          return map;
        }, new Map()) ?? new Map()
      );
    }, [shadowValueBySignalIdMap, state.instances]);

  const handleCompleteMaintenanceSuccess = useCallback(async () => {
    fetchReminderInstances(refreshSource);
  }, [fetchReminderInstances, refreshSource]);

  useEffect(() => {
    if (!widget.settings?.maintenance?.length) return;

    const source = CancelToken.source();
    fetchReminderInstances(source);
    return source.cancel;
  }, [fetchReminderInstances, widget.settings?.maintenance]);

  useEffect(() => refreshSource?.cancel, []);

  return (
    <>
      <div className="w-100 flex-grow-1">
        {state.instancesLoading ? (
          <div className="h-100 w-100 d-flex align-items-center justify-content-center">
            <BrandPrimarySpinningCircle size="3x" />
          </div>
        ) : (
          <>
            <div className="row sticky-top bg-white py-2">
              <p className="mb-0 col-6" />
              <p className="mb-0 col-3 px-0 text-center text-nowrap nine-tenth-text text-dark font-weight-bold">
                Status
              </p>
              <p className="mb-0 col-3 pl-0 text-center text-nowrap nine-tenth-text text-dark font-weight-bold">
                Last Service
              </p>
            </div>
            {sortedInstances}
            <div className="row py-2">
              <p className="mb-0 col-12 text-right text-danger nine-tenth-text">
                {state.error}
              </p>
            </div>
          </>
        )}
      </div>
      <CompleteMaintenanceModal
        checkedInstances={checkedInstances}
        handleCompleteMaintenanceSuccess={handleCompleteMaintenanceSuccess}
        instanceShadowValueMap={instanceShadowValueMap}
        isOpen={!!state.showCompleteMaintenanceModal}
        signalUnitMap={signalUnitsMap}
        toggleModal={() => toggleCompleteMaintenanceModal('')}
      />
    </>
  );
}

MaintenanceWidget.propTypes = {
  asset: PropTypes.object,
  shadow: PropTypes.object,
  signalUnitsMap: PropTypes.object,
  widget: PropTypes.object,
};

function TimeLengthButton({ instance, toggleCompleteMaintenanceModal }) {
  const { color, text } = useMemo(() => {
    switch (instance?.MaintenanceNotifications?.[0]?.status) {
      case DUE:
        return { color: 'danger', text: 'Due' };
      case UPCOMING:
        return { color: 'warning', text: 'Upcoming' };
      default:
        return { color: 'success', text: 'Ok' };
    }
  }, [instance?.MaintenanceNotifications]);

  return (
    <Button
      className={`p-0 nine-tenth-text text-${color}`}
      color="link"
      onClick={() => toggleCompleteMaintenanceModal(instance?._id)}
    >
      {text}
    </Button>
  );
}
TimeLengthButton.propTypes = {
  instance: PropTypes.object,
  toggleCompleteMaintenanceModal: PropTypes.func,
};
